import { useEffect, useMemo, useRef, useState } from 'react'
import { BOX_TRUCK_NEARBY_DRIVERS_PAGE_SIZE, BoxTruckNearbyDriversSort, BoxTruckNearbyOrigin, CanonicalLoad } from '@numeo/types-core'
import { LoadBid } from '@numeo/types'
import { useAppSelector } from 'hooks'
import { orderNearbyDrivers } from '../utils/orderNearbyDrivers'
import { readNearbyDriversSort, writeNearbyDriversSort } from '../utils/nearbyDriversSortStorage'
import { syntheticPinnedRow } from '../utils/nearbyPinnedRow'
import { type BoxTruckNearbyDriversSurface, type BoxTruckNearbySortOutcome, trackBoxTruckNearbySortChanged } from '../utils/analytics'
import type { BoxTruckVerification, NearbyBoxTruckDriver } from '../services/boxTruckLoadsApi'
import { fetchNearbyBoxTruckDrivers } from '../services/nearbyDriversApi'
import { NEARBY_BID_REFRESH_DEBOUNCE_MS, NEARBY_RADIUS_DEBOUNCE_MS } from '../constants/nearbyDrivers'

interface NearbyDriversPageInput {
    load?: CanonicalLoad
    origin?: { lat: number; lng: number } | null
    radiusMi: number
    bids?: LoadBid[]
    pinnedDrivers: BoxTruckVerification[]
    onDriversChange?: (drivers: NearbyBoxTruckDriver[], nearestKnown: boolean) => void
}

/**
 * The Nearby Drivers list itself: which drivers are in it (the radius search, its pages, the picked drivers it
 * has to rescue), in what order, and what the host is told. The tab only draws it.
 *
 * Returns five groups: the rows, the sort, the first page's status, the paging, and the search origin.
 */
export const useNearbyDriversPage = ({ load, origin: originProp, radiusMi, bids, pinnedDrivers, onDriversChange }: NearbyDriversPageInput) => {
    const pinnedIds = useMemo(() => pinnedDrivers.map((d) => d.userId), [pinnedDrivers])
    // The fetch effect below keys off these STRINGS, never the array identity — a caller that rebuilt the
    // prop array each render would otherwise refetch on every parent render.
    const pinnedIdsKey = useMemo(() => [...pinnedIds].sort().join(','), [pinnedIds])
    // Built from the KEY for the same reason: this set feeds the list reported to `onDriversChange`, and a set
    // rebuilt per parent render made that list new per render — see `bidderIds` below for the loop it caused.
    const pinnedIdSet = useMemo(() => new Set(pinnedIdsKey ? pinnedIdsKey.split(',') : []), [pinnedIdsKey])
    const pinnedDriversRef = useRef(pinnedDrivers)
    pinnedDriversRef.current = pinnedDrivers
    // Opens on the operator's last sort (Location until they pick one), read once at mount so the first page is
    // already fetched in it. A click saves it for the next open, in the load drawer and in Search Vehicles alike.
    const userId = useAppSelector((state) => state.auth.user?.id)
    const [sort, setSort] = useState(() => readNearbyDriversSort(userId))
    // Set by a header click (with where the tab is open) and taken by the next fetch run, which reports how that
    // click's page 1 ended. Only a click arms it, so a bids or origin refresh can never report a sort change.
    const sortClickRef = useRef<BoxTruckNearbyDriversSurface | null>(null)
    const changeSort = (next: BoxTruckNearbyDriversSort) => {
        if (next === sort) return
        sortClickRef.current = load ? 'load_drawer' : 'search_vehicles'
        writeNearbyDriversSort(userId, next)
        setSort(next)
    }
    const [drivers, setDrivers] = useState<NearbyBoxTruckDriver[]>([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(false)
    const [hasMore, setHasMore] = useState(false)
    const [nextCursor, setNextCursor] = useState<string | null>(null)
    const [loadingMore, setLoadingMore] = useState(false)
    const [loadMoreError, setLoadMoreError] = useState(false)
    // A sort click's page 1 is on its way while the old rows stay on screen (see the fetch effect), and whether
    // that request failed — shown inline so the rows and the sort headers stay.
    const [reordering, setReordering] = useState(false)
    const [reorderError, setReorderError] = useState(false)
    // The radius's nearest driver, which a `recent` page 1 carries because its rows are the newest locations,
    // not the nearest. Feeds only what the host is told (see `reportedDrivers`); it is not a table row.
    const [nearestInRadius, setNearestInRadius] = useState<NearbyBoxTruckDriver | null>(null)
    // The server could not read that driver, so the radius's nearest is unknown — not "none".
    const [nearestUnavailable, setNearestUnavailable] = useState(false)

    const lat = load?.pickupLat ?? originProp?.lat ?? null
    const lng = load?.pickupLon ?? originProp?.lng ?? null
    const origin = useMemo(() => (lat != null && lng != null ? { lat, lng } : null), [lat, lng])
    const originCity = load?.pickupCity?.trim() ?? ''
    const originState = load?.pickupStateProv?.trim() ?? ''
    const hasNamedOrigin = originCity !== '' && originState !== ''
    const namedOriginKey = `${originCity}|${originState}`
    const [resolvedNamedOrigin, setResolvedNamedOrigin] = useState<{ key: string; origin: BoxTruckNearbyOrigin } | null>(null)
    const resolvedNamedOriginRef = useRef(resolvedNamedOrigin)
    resolvedNamedOriginRef.current = resolvedNamedOrigin
    const effectiveOrigin = origin ?? (resolvedNamedOrigin?.key === namedOriginKey ? resolvedNamedOrigin.origin : null)

    // This load's BIDDERS are forced into the list whatever the radius: a bid is a fact about this load, not
    // a proximity guess, and dropping one because the driver sits far away would take a real offer off the
    // operator's screen. The tab's picked drivers are deliberately NOT forced in — the radius is the question
    // the operator asked, so it applies to them too and a pick outside it simply doesn't appear. (It used to
    // include them, which put a 927-mile truck on a 150-mile search.) The sorted-joined key keeps the effect
    // below from refetching when the bids array is merely a new identity.
    const includeIdsKey = useMemo(() => [...new Set((bids ?? []).map((b) => b.bidderId).filter(Boolean))].sort().join(','), [bids])
    // From the key, never from `bids` itself. Hosts pass a fresh array per render (the drawer's `bids = []`
    // default, the notifications' `openBid ? [openBid] : []`), and this set feeds the list the host stores from
    // `onDriversChange`: a new set per render made a new list per render, the host stored it, re-rendered with
    // a new `bids`, and so on forever. The key only changes when the bidder IDS change.
    const bidderIds = useMemo(() => new Set(includeIdsKey ? includeIdsKey.split(',') : []), [includeIdsKey])
    // Everything the current page depends on. `operatorIntentKey` in the fetch effect is this minus
    // `includeIdsKey` — a new input added here that the operator controls belongs in both, or a change to it
    // would be mistaken for an incoming bid and settle two seconds late.
    const queryKey = `${lat ?? ''}|${lng ?? ''}|${originCity}|${originState}|${radiusMi}|${sort}|${includeIdsKey}|${pinnedIdsKey}`
    const activeQueryKeyRef = useRef(queryKey)
    useEffect(() => {
        activeQueryKeyRef.current = queryKey
    }, [queryKey])
    // The origin this tab last searched around. A new one means the drawer just opened (or switched load) —
    // the fetch the operator is waiting on — so it goes out immediately instead of through the radius debounce.
    const originKeyRef = useRef<string | null>(null)
    // Everything in the query the OPERATOR controls: where, how far, in what order, and who they picked. When this is
    // unchanged and the effect still ran, the only input that moved was the bid set — news, not a request —
    // which is what earns the longer settle time below.
    const operatorIntentKeyRef = useRef<string | null>(null)
    // The same minus the sort: when only this is unchanged, the run was a sort-header click.
    const intentBesidesSortRef = useRef<string | null>(null)

    useEffect(() => {
        // Depend on raw origin/radius PRIMITIVES, not an object identity — the correctness is
        // then local (a lost/misconfigured memo can't push this into a re-fetch loop).
        let cancelled = false
        // Taken on every run: a click whose run turned out to be a bigger search (no origin, a new one) is not
        // reported later by some unrelated run.
        const clickedFrom = sortClickRef.current
        sortClickRef.current = null
        if ((lat == null || lng == null) && !hasNamedOrigin) {
            // No origin to search around — the pinned driver must STILL be visible, so render just them.
            // Reset the flight flags too: a cancelled previous fetch (load switch mid-debounce) can leave
            // loading/error stale, and this branch's render REACHES those states now.
            setLoading(false)
            setError(false)
            setHasMore(false)
            setNextCursor(null)
            setLoadingMore(false)
            setLoadMoreError(false)
            setReordering(false)
            setReorderError(false)
            setNearestInRadius(null)
            setNearestUnavailable(false)
            originKeyRef.current = null
            const pinned = pinnedDriversRef.current
            const pinnedRows = pinned.map((v) => syntheticPinnedRow(v, null))
            setDrivers(pinnedRows)
            // Guarded like the debounced branch below — a load switch mid-flight must not merge the previous
            // load's counts into the new one's badge state.
            return () => {
                cancelled = true
            }
        }
        // A new origin is the search the drawer just opened for, with the operator in front of a spinner —
        // it skips the debounce below, which exists for a radius that changes several times a second.
        const originKey = lat != null && lng != null ? `${lat}|${lng}` : namedOriginKey
        const intentBesidesSort = `${originKey}|${radiusMi}|${pinnedIdsKey}`
        const operatorIntentKey = `${intentBesidesSort}|${sort}`
        const isNewOrigin = originKeyRef.current !== originKey
        const bidsOnly = !isNewOrigin && operatorIntentKeyRef.current === operatorIntentKey
        // A sort click is one discrete request, not a dial being dragged, so it skips the radius debounce too.
        const sortOnly = !isNewOrigin && !bidsOnly && intentBesidesSortRef.current === intentBesidesSort
        const delayMs = isNewOrigin || sortOnly ? 0 : bidsOnly ? NEARBY_BID_REFRESH_DEBOUNCE_MS : NEARBY_RADIUS_DEBOUNCE_MS
        // Reports once: whichever of landed, failed or cancelled comes first.
        let sortReport: ((outcome: BoxTruckNearbySortOutcome) => void) | null =
            sortOnly && clickedFrom
                ? (outcome) => {
                      sortReport = null
                      trackBoxTruckNearbySortChanged(sort, clickedFrom, outcome)
                  }
                : null
        originKeyRef.current = originKey
        operatorIntentKeyRef.current = operatorIntentKey
        intentBesidesSortRef.current = intentBesidesSort
        // A bids-only refresh changes NOTHING until its page lands. The operator did not ask for it, so the
        // list they are reading has to stay as it is — the spinner, yes, but also whether "Load more" is
        // there and whether a page is already on its way. Clearing these up front while the refetch waits
        // two seconds took the pagination control away for those two seconds and cleared the loading flag
        // of a `loadMore` that was still running, on every incoming bid.
        if (sortOnly) {
            // The rows stay on screen — the same drivers, re-sorted at once by `ordered` — and the sort headers
            // the operator just clicked stay with them; page 1 of the new order replaces them when it lands.
            // Swapping the table for "Loading…" took the clicked control away for the whole round trip. Paging
            // DOES reset now: the cursor belongs to the old order, and the server refuses it under the new one.
            setError(false)
            setHasMore(false)
            setNextCursor(null)
            setLoadingMore(false)
            setLoadMoreError(false)
            // Without a cue the old rows read as the complete new list: no "+", no "Load more", no spinner.
            setReordering(true)
            setReorderError(false)
        } else if (!bidsOnly) {
            setLoading(true)
            setError(false)
            setHasMore(false)
            setNextCursor(null)
            setLoadingMore(false)
            setLoadMoreError(false)
            setReordering(false)
            setReorderError(false)
        }
        // Debounce a radius still being adjusted, so it doesn't fire a request per tick (see delayMs above).
        const t = setTimeout(async () => {
            try {
                const cachedNamedOrigin = resolvedNamedOriginRef.current?.key === originKey ? resolvedNamedOriginRef.current.origin : null
                const requestOrigin = lat != null && lng != null ? { lat, lng } : cachedNamedOrigin
                const page = await fetchNearbyBoxTruckDrivers(
                    requestOrigin?.lat ?? null,
                    requestOrigin?.lng ?? null,
                    radiusMi,
                    includeIdsKey ? includeIdsKey.split(',') : undefined,
                    { limit: BOX_TRUCK_NEARBY_DRIVERS_PAGE_SIZE },
                    hasNamedOrigin ? { city: originCity, state: originState } : undefined,
                    sort
                )
                const rows = page.drivers
                const pageOrigin = page.origin ?? requestOrigin
                // Order is applied at render (`ordered` below), so this only decides WHICH rows are listed.
                // If the server couldn't supply a picked driver for a data reason, synthesize the row from
                // the picker snapshot when its last location proves that it is inside this radius.
                const pinned = pinnedDriversRef.current
                // A pinned driver the radius query didn't return is either outside it or has no location at
                // all — so synthesize only the ones we can PROVE are inside it. That still rescues an in-range
                // pick the server omitted for a data reason (stale claim, missing verification row) without
                // reinstating the old blanket "visible no matter what".
                const missing = pinned
                    .filter((v) => !rows.some((r) => r.driverId === v.userId))
                    .map((v) => syntheticPinnedRow(v, pageOrigin))
                    .filter((row) => row.proximityDistanceMi != null && row.proximityDistanceMi <= radiusMi)
                if (!cancelled) {
                    if (lat == null && lng == null && page.origin) {
                        const resolved = { key: originKey, origin: page.origin }
                        resolvedNamedOriginRef.current = resolved
                        setResolvedNamedOrigin(resolved)
                    }
                    setDrivers(missing.length > 0 ? [...missing, ...rows] : rows)
                    setNearestInRadius(page.nearest)
                    setNearestUnavailable(page.nearestUnavailable)
                    setReorderError(false)
                    setHasMore(page.hasMore)
                    setNextCursor(page.nextCursor)
                    // A landed page settles the whole list, including the flags the bids-only branch above
                    // deliberately did not clear up front. `loadMore` abandons a page whose query key has
                    // moved on — and skips its own `setLoadingMore(false)` for the same reason — so without
                    // this a bid arriving mid-`loadMore` left the button spinning for the rest of the
                    // drawer session, and a previous error stayed up over fresh rows.
                    setLoadingMore(false)
                    setLoadMoreError(false)
                    setError(false)
                    sortReport?.('loaded')
                }
            } catch (err) {
                console.error('[box-truck] fetchNearbyBoxTruckDrivers failed', err)
                // A failed re-sort keeps the rows already on screen; replacing the table would take the sort
                // headers away with it. Any other failed search has nothing current to show.
                if (!cancelled) {
                    if (sortOnly) setReorderError(true)
                    else setError(true)
                    sortReport?.('failed')
                }
            } finally {
                if (!cancelled) {
                    setLoading(false)
                    setReordering(false)
                }
            }
        }, delayMs)
        return () => {
            cancelled = true
            clearTimeout(t)
            sortReport?.('abandoned')
        }
    }, [lat, lng, originCity, originState, hasNamedOrigin, namedOriginKey, radiusMi, sort, includeIdsKey, pinnedIdsKey])

    const loadMore = async () => {
        if (((lat == null || lng == null) && !hasNamedOrigin) || nextCursor == null || loadingMore) return
        const requestedQueryKey = queryKey
        setLoadingMore(true)
        setLoadMoreError(false)
        try {
            const page = await fetchNearbyBoxTruckDrivers(
                effectiveOrigin?.lat ?? null,
                effectiveOrigin?.lng ?? null,
                radiusMi,
                undefined,
                { cursor: nextCursor, limit: BOX_TRUCK_NEARBY_DRIVERS_PAGE_SIZE },
                hasNamedOrigin ? { city: originCity, state: originState } : undefined,
                sort
            )
            // A load/radius change can finish an older page after the new first page. Drop it instead of
            // mixing two searches; the initial fetch effect owns the new query's state.
            if (activeQueryKeyRef.current !== requestedQueryKey) return
            const seen = new Set(drivers.map((driver) => driver.driverId))
            const added = page.drivers.filter((driver) => !seen.has(driver.driverId))
            setDrivers((current) => {
                const currentIds = new Set(current.map((driver) => driver.driverId))
                return [...current, ...added.filter((driver) => !currentIds.has(driver.driverId))]
            })
            setHasMore(page.hasMore)
            setNextCursor(page.nextCursor)
        } catch (err) {
            console.error('[box-truck] fetchNearbyBoxTruckDrivers page failed', err)
            if (activeQueryKeyRef.current === requestedQueryKey) setLoadMoreError(true)
        } finally {
            if (activeQueryKeyRef.current === requestedQueryKey) setLoadingMore(false)
        }
    }

    // The list as shown. Derived rather than stored so a new bid, a location edit or a sort click reorders it
    // without a refetch — the server orders the radius, this places the rows it did not (see the helper).
    const ordered = useMemo(() => orderNearbyDrivers(drivers, sort, bidderIds, pinnedIdSet), [drivers, sort, bidderIds, pinnedIdSet])

    // What the host receives is ranked by DISTANCE, whatever the operator sorts the table by: bidders, then
    // picked drivers, then nearest first. The load drawer reads its first row twice — it preselects it when it
    // is a bidder, and it is the Offer email's unit-details fallback when nobody is ticked — and both mean the
    // nearest truck. Handing over the display order put the truck that pinged most recently, maybe 140 mi
    // out, into the broker's email instead of the one 12 mi from pickup.
    //
    // Ranking what was fetched is not enough under `recent`: page 1 is the newest locations, and the nearest truck
    // may have last pinged days ago and be on no page yet. The server sends that one driver with a `recent` page
    // 1, and it joins this list (not the table) when the fetched rows do not already hold it.
    const reportedDrivers = useMemo(() => {
        const withNearest = nearestInRadius && !drivers.some((d) => d.driverId === nearestInRadius.driverId) ? [...drivers, nearestInRadius] : drivers
        return orderNearbyDrivers(withNearest, BoxTruckNearbyDriversSort.DISTANCE, bidderIds, pinnedIdSet)
    }, [drivers, nearestInRadius, bidderIds, pinnedIdSet])
    useEffect(() => {
        onDriversChange?.(reportedDrivers, !nearestUnavailable)
    }, [reportedDrivers, nearestUnavailable, onDriversChange])

    // Patch one driver in place after an inline availability/note save, so the row reflects the change.
    const updateDriver = (driverId: string, patch: Partial<NearbyBoxTruckDriver>) =>
        setDrivers((prev) => prev.map((d) => (d.driverId === driverId ? { ...d, ...patch } : d)))

    return {
        list: { rows: ordered, pinnedIdSet, updateDriver },
        sorting: { sort, changeSort, reordering, reorderError },
        status: { loading, error },
        paging: { hasMore, loadingMore, loadMoreError, loadMore },
        // `searchable`: there is something to search around, a point or a city/state the server resolves.
        origin: { effective: effectiveOrigin, searchable: effectiveOrigin != null || hasNamedOrigin },
    }
}
